import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

import Navigation from '../../components/Navigation';
import { CheckCircle, Mail, AlertCircle, Lock, UserCircle } from 'lucide-react';
import StandardInput from '../../components/ui/StandardInput';
import StandardButton from '../../components/ui/StandardButton';
import StandardCard from '../../components/ui/StandardCard';

import StandardSelect from '../../components/ui/StandardSelect';

const Register = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [statementAccepted, setStatementAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminRequestSent, setAdminRequestSent] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('[Register] ========== SUBMIT STARTED ==========');
    console.log('[Register] Form values:', { email, companyId, role, statementAccepted });
    setError('');
    setLoading(true);
    console.log('[Register] Loading state set to TRUE');

    try {
      console.log('[Register] Starting validation...');
      // Basic validation
      if (!email?.trim()) {
        console.log('[Register] Validation failed: Email missing');
        setError(t('register.errors.emailRequired'));
        setLoading(false);
        return;
      }

      if (!password?.trim()) {
        console.log('[Register] Validation failed: Password missing');
        setError(t('register.errors.passwordRequired'));
        setLoading(false);
        return;
      }

      if (password?.length < 8) {
        console.log('[Register] Validation failed: Password too short');
        setError(t('register.errors.passwordTooShort'));
        setLoading(false);
        return;
      }

      if (!statementAccepted) {
        console.log('[Register] Validation failed: Statement not accepted');
        setError(t('register.errors.statementRequired'));
        setLoading(false);
        return;
      }

      // ADMIN ROLE PROTECTION: Block automatic admin registration
      // This check must happen BEFORE any login/signup attempt
      if (role === 'admin') {
        setLoading(false);
        setAdminRequestSent(true);
        return;
      }

      console.log('[Register] Validation passed ✓');
      console.log('[Register] Attempting LOGIN first...');
      
      // UNIFIED FLOW: Try login first (faster, most common case)
      try {
        console.log('[Register] Calling signIn function...');
        const signInResult = await signIn(email, password);
        console.log('[Register] signIn returned successfully:', signInResult);
        console.log('[Register] Login successful - preparing navigation');
        
        console.log('[Register] Setting loading to FALSE after successful login');
        setLoading(false);
        console.log('[Register] Loading state set to FALSE');
        
        console.log('[Register] Scheduling navigation to /berza...');
        setTimeout(() => {
          console.log('[Register] Executing navigation NOW');
          navigate('/berza');
          console.log('[Register] Navigation called');
        }, 0);
        console.log('[Register] setTimeout scheduled, returning from handleSubmit');
        return;
      } catch (loginError) {
        console.log('[Register] Login failed with error:', loginError?.message);
        const errorMsg = loginError?.message?.toLowerCase() || '';
        const isUserNotFound = errorMsg?.includes('invalid') || errorMsg?.includes('not found') || errorMsg?.includes('credentials');
        
        console.log('[Register] Error analysis:', { errorMsg, isUserNotFound });
        
        if (!isUserNotFound) {
          console.log('[Register] Non-auth error detected, throwing...');
          throw loginError;
        }
        
        console.log('[Register] User not found - proceeding to REGISTRATION');
      }

      console.log('[Register] Starting registration process...');
      const { supabase } = await import('../../lib/supabase');
      console.log('[Register] Supabase imported');
      
      // SECURITY: Always register as 'user' - admin role can NEVER be self-assigned
      const safeRole = 'user';
      console.log('[Register] Safe role for registration:', safeRole);
      
      console.log('[Register] Calling supabase.auth.signUp...');
      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            company_id: companyId,
            role: safeRole,
            full_name: companyId
          }
        }
      });
      console.log('[Register] signUp returned:', { data, error: signUpError });

      if (signUpError) {
        console.log('[Register] SignUp error detected:', signUpError?.message);
        if (signUpError?.message?.includes('Signups not allowed')) {
          setError(t('register.errors.signupsDisabled'));
        } else if (signUpError?.message?.includes('already registered')) {
          setError(t('register.errors.emailExists'));
        } else {
          setError(signUpError?.message || t('register.errors.registrationError'));
        }
        console.log('[Register] Setting loading to FALSE after signUp error');
        setLoading(false);
        return;
      }

      console.log('[Register] Registration successful!');
      if (data?.session) {
        console.log('[Register] Session exists, navigating to dashboard');
        setLoading(false);
        setTimeout(() => {
          console.log('[Register] Executing navigation to /berza');
          navigate('/berza');
        }, 0);
      } else {
        console.log('[Register] No session - email confirmation required');
        setError(t('register.errors.emailConfirmation'));
        setLoading(false);
      }

    } catch (err) {
      console.error('[Register] CATCH BLOCK - Error:', err);
      console.error('[Register] Error details:', { message: err?.message, stack: err?.stack });
      setError(err?.message || t('register.errors.authError'));
      console.log('[Register] Setting loading to FALSE in catch block');
      setLoading(false);
    }
    
    console.log('[Register] ========== SUBMIT ENDED ==========');
  };

  if (adminRequestSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation />
        <div className="flex-1 flex items-center justify-center px-4">
          <StandardCard className="max-w-md w-full text-center">
            <div className="mb-6">
              <Mail className="text-blue-600 mx-auto" size={64} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('register.adminRequestTitle')}
            </h2>
            <p className="text-gray-600 mb-4 leading-relaxed">
              {t('register.adminRequestDesc1')}
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {t('register.adminRequestDesc2')}
            </p>
            <a
              href="mailto:admin@valuebook.rs"
              className="inline-block text-blue-700 font-semibold text-lg hover:underline mb-6"
            >
              admin@valuebook.rs
            </a>
            <div className="mt-2 mb-4">
              <a
                href={`mailto:admin@valuebook.rs?subject=${encodeURIComponent('Zahtev za registraciju administratora')}&body=${encodeURIComponent('Poštovani,\n\nPodnosim zahtev za dobijanje administratorskog naloga na ValueBook platformi.\n\nMoji podaci:\nIme: ' + companyId + '\nEmail: ' + email + '\n\nMolim Vas da razmotrite moj zahtev.\n\nS poštovanjem')}`}
                className="block w-full"
              >
                <StandardButton className="w-full min-h-[48px] bg-blue-600 hover:bg-blue-700">
                  <Mail size={18} className="mr-2 inline" />
                  {t('register.adminRequestBtn')}
                </StandardButton>
              </a>
            </div>
            <div className="mt-4">
              <StandardButton
                className="w-full min-h-[48px]"
                variant="outline"
                onClick={() => { setAdminRequestSent(false); setRole('user'); }}
              >
                {t('register.backToRegister')}
              </StandardButton>
            </div>
          </StandardCard>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation />
        <div className="flex-1 flex items-center justify-center px-4">
        <StandardCard className="max-w-md w-full text-center">
          <div className="mb-6">
            <CheckCircle className="text-green-600 mx-auto" size={64} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('register.successTitle')}
          </h2>
          <p className="text-gray-600 mb-4">
            {t('register.successDesc')}
          </p>
        </StandardCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('register.title')}
            </h1>
          </div>

          <StandardCard className="">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <StandardInput
                label={t('register.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder={t('register.emailPlaceholder')}
                icon={<Mail size={20} />}
                required
                disabled={loading}
                error=""
                helperText=""
                className=""
              />

              <StandardInput
                label={t('register.nameLabel')}
                type="text"
                value={companyId}
                onChange={(e) => setCompanyId(e?.target?.value)}
                placeholder={t('register.namePlaceholder')}
                icon={<UserCircle size={20} />}
                disabled={loading}
                error=""
                helperText=""
                className=""
              />

              <StandardInput
                label={t('register.passwordLabel')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                placeholder={t('register.passwordPlaceholder')}
                icon={<Lock size={20} />}
                required
                disabled={loading}
                error=""
                helperText=""
                className=""
              />

              <StandardSelect
                label={t('register.roleLabel')}
                value={role}
                onChange={(e) => setRole(e?.target?.value)}
                options={[
                  { value: 'user', label: t('register.roleUser') },
                  { value: 'admin', label: t('register.roleAdmin') }
                ]}
                icon={<UserCircle size={20} />}
                disabled={loading}
                error=""
                helperText=""
                className=""
              />

              {role === 'admin' && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-amber-800 leading-relaxed">
                    {t('register.adminWarning')}
                  </p>
                </div>
              )}

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="statementAccepted"
                  checked={statementAccepted}
                  onChange={(e) => setStatementAccepted(e?.target?.checked)}
                  disabled={loading}
                  className="mt-1 h-4 w-4 min-w-[16px] rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="statementAccepted" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                  {t('register.termsText')}{' '}
                  <a
                    href="/uslovi-koriscenja"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                    onClick={(e) => e?.stopPropagation()}
                  >
                    {t('register.termsLink')}
                  </a>{' '}
                  {t('register.termsSuffix')}
                </label>
              </div>

              <StandardButton
                type="submit"
                className="w-full min-h-[48px]"
                disabled={loading}
              >
                {loading ? t('register.processing') : t('register.submitBtn')}
              </StandardButton>

              <div className="mt-4 space-y-2">
                <p className="text-xs text-gray-500 leading-relaxed">
                  ℹ️ {t('register.iframeNote1')}
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  ℹ️ {t('register.iframeNote2')}
                </p>
              </div>
            </form>
          </StandardCard>
        </div>
      </div>
    </div>
  );
};

export default Register;